import { WebAuthnApiError } from "@src/features/webauthn/lib/webAuthnCredentialsAPI"
import { WebAuthnErrorCode } from "@src/features/webauthn/types/webAuthnTypes"
import { toError } from "@src/utils/errors"

export type PasskeyLoginFailureType =
  | "cancelled"
  | "credential_not_found"
  | "not_supported"
  | "backend_error"
  | "technical_error"
  | "unknown"

/** User-initiated failures that should NOT trigger Sentry alerts. */
const BENIGN_PASSKEY_FAILURES: ReadonlySet<PasskeyLoginFailureType> = new Set([
  "cancelled",
  "credential_not_found",
  "not_supported",
])

export function isActionablePasskeyFailure(
  type: PasskeyLoginFailureType
): boolean {
  return !BENIGN_PASSKEY_FAILURES.has(type)
}

export function classifyPasskeyLoginFailure(
  error: unknown
): PasskeyLoginFailureType {
  if (
    error instanceof WebAuthnApiError &&
    error.code === WebAuthnErrorCode.CREDENTIAL_NOT_FOUND
  ) {
    return "credential_not_found"
  }

  if (error instanceof DOMException) {
    if (error.name === "AbortError" || error.name === "NotAllowedError") {
      return "cancelled"
    }
    if (error.name === "NotSupportedError") {
      return "not_supported"
    }
  }

  const message = toError(error).message.toLowerCase()

  if (
    message.includes("cancel") ||
    message.includes("aborted") ||
    message.includes("notallowederror") ||
    message.includes("timed out")
  ) {
    return "cancelled"
  }

  if (
    message.includes("not supported") ||
    message.includes("unsupported") ||
    message.includes("notsupportederror")
  ) {
    return "not_supported"
  }

  if (
    message.includes("401") ||
    message.includes("403") ||
    message.includes("500") ||
    message.includes("backend") ||
    message.includes("server") ||
    message.includes("network") ||
    message.includes("fetch")
  ) {
    return "backend_error"
  }

  if (message.trim().length > 0) {
    return "technical_error"
  }

  return "unknown"
}
